import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import {
  getStoredQuotes,
  formatWhatsAppFollowUpUrl,
  formatEmailFollowUpUrl,
  type UserQuoteRecord,
  type QuoteStatus,
} from "@/lib/user-quotes";
import {
  ClipboardList,
  Search,
  MessageCircle,
  Mail,
  ExternalLink,
  Clock,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Calendar,
  Building2,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { localizedDictionaryMeta } from "@/lib/utils/seo";

export const Route = createFileRoute("/account/quotes")({
  head: () => ({
    meta: localizedDictionaryMeta(
      "account.quotes.title",
      "account.quotes.subtitle",
      "Quotes & Enquiries",
      "Track all submitted commercial RFQs, technical specifications, and 1-click follow-up.",
    ),
  }),
  component: AccountQuotesPage,
});

const STATUS_CONFIG: Record<
  QuoteStatus,
  { badge: string; icon: typeof Clock; labelKey: string; fallbackLabel: string }
> = {
  Submitted: {
    badge: "bg-amber-500/10 text-amber-700 border-amber-500/30",
    icon: Clock,
    labelKey: "account.quotes.status.submitted",
    fallbackLabel: "Submitted",
  },
  "Under Review": {
    badge: "bg-sky-500/10 text-sky-700 border-sky-500/30",
    icon: ShieldCheck,
    labelKey: "account.quotes.status.review",
    fallbackLabel: "Under Review",
  },
  Quoted: {
    badge: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
    icon: CheckCircle2,
    labelKey: "account.quotes.status.quoted",
    fallbackLabel: "Quoted",
  },
  Closed: {
    badge: "bg-muted text-muted-foreground border-border/80",
    icon: AlertCircle,
    labelKey: "account.quotes.status.closed",
    fallbackLabel: "Closed",
  },
};

function AccountQuotesPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [quotes, setQuotes] = useState<UserQuoteRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<string>("all");
  const [expandedQuotes, setExpandedQuotes] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let active = true;

    async function loadQuotes() {
      // 1. First retrieve local stored submissions for this user's email
      const localQuotes = getStoredQuotes(user?.email);

      // 2. Fetch any server-side quotes from the BFF endpoint
      try {
        const res = await fetch("/api/portal/quotes");
        if (res.ok && active) {
          const json = await res.json();
          const serverQuotes = Array.isArray(json.data) ? (json.data as UserQuoteRecord[]) : [];

          // Merge server quotes with local quotes (local takes precedence or deduplicates by id)
          const merged = [...localQuotes];
          for (const sq of serverQuotes) {
            if (!merged.some((m) => m.id.toLowerCase() === sq.id.toLowerCase())) {
              merged.push(sq);
            }
          }
          setQuotes(merged);
          return;
        }
      } catch (err) {
        console.warn("Failed to fetch server quotes, using local records:", err);
      }

      if (active) {
        setQuotes(localQuotes);
        setLoading(false);
      }
    }

    loadQuotes().finally(() => {
      if (active) setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [user?.email]);

  const toggleExpand = (id: string) => {
    setExpandedQuotes((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const filteredQuotes = useMemo(() => {
    return quotes.filter((q) => {
      // Tab status filter
      if (activeTab !== "all" && q.status.toLowerCase() !== activeTab.toLowerCase()) {
        return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesId = q.id.toLowerCase().includes(query);
        const matchesTitle = (q.productTitle || "").toLowerCase().includes(query);
        const matchesCategory = (q.category || "").toLowerCase().includes(query);
        const matchesCompany = (q.contact?.company || "").toLowerCase().includes(query);
        const matchesItems = q.items?.some((it) => it.title.toLowerCase().includes(query)) ?? false;
        return matchesId || matchesTitle || matchesCategory || matchesCompany || matchesItems;
      }

      return true;
    });
  }, [quotes, activeTab, searchQuery]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-black text-navy flex items-center gap-3">
            <ClipboardList className="h-8 w-8 text-primary" />
            {t("account.quotes.title") || "Quotes & Inquiries"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            {t("account.quotes.subtitle") ||
              "Track your submitted quotation requests, view technical specs, and follow up directly with your VARS account manager."}
          </p>
        </div>
        <Link to="/request-quote">
          <Button className="rounded-xl font-bold gap-2 shadow-sm">
            <Sparkles className="h-4 w-4" />
            {t("account.quotes.newRequest") || "New Quote Request"}
          </Button>
        </Link>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1 sm:pb-0">
          {[
            { id: "all", label: t("account.quotes.filter.all") || "All Requests" },
            { id: "submitted", label: t("account.quotes.filter.submitted") || "Submitted" },
            { id: "under review", label: t("account.quotes.filter.review") || "Under Review" },
            { id: "quoted", label: t("account.quotes.filter.quoted") || "Quoted" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap",
                activeTab === tab.id
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "bg-surface-alt/60 text-navy hover:bg-surface-alt hover:text-navy border border-border/60",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("account.quotes.searchPlaceholder") || "Search by reference or product…"}
            className="pl-9 h-9 text-xs rounded-xl"
          />
        </div>
      </div>

      {/* Quote List */}
      {loading ? (
        <div className="glass-card rounded-2xl p-12 text-center text-muted-foreground text-sm border border-border/60">
          {t("account.quotes.loading") || "Loading quotes…"}
        </div>
      ) : filteredQuotes.length === 0 ? (
        <div className="glass-card rounded-2xl p-12 text-center border border-border/60 space-y-4">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ClipboardList className="h-8 w-8" />
          </div>
          <div className="space-y-1 max-w-md mx-auto">
            <h3 className="font-display text-lg font-bold text-navy">
              {t("account.quotes.emptyTitle") || "No Quote Requests Found"}
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {searchQuery || activeTab !== "all"
                ? t("account.quotes.emptyFiltered") ||
                  "No quote requests match your filter criteria."
                : t("account.quotes.emptyDesc") ||
                  "You haven't submitted any commercial quotes or inquiries yet. Explore our export catalog or send an RFQ to get tailored pricing and delivery schedules."}
            </p>
          </div>
          <div className="pt-2 flex items-center justify-center gap-3">
            <Link to="/products">
              <Button variant="outline" className="rounded-xl text-xs font-bold">
                {t("nav.catalog") || "Browse Products"}
              </Button>
            </Link>
            <Link to="/request-quote">
              <Button className="rounded-xl text-xs font-bold gap-1.5">
                {t("account.quotes.requestNow") || "Request a Quote"}
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredQuotes.map((quote) => {
            const statusConfig = STATUS_CONFIG[quote.status] || STATUS_CONFIG.Submitted;
            const StatusIcon = statusConfig.icon;
            const isExpanded = !!expandedQuotes[quote.id];
            const hasSpecs = quote.customFields && Object.keys(quote.customFields).length > 0;
            const waUrl = formatWhatsAppFollowUpUrl(quote);
            const mailUrl = formatEmailFollowUpUrl(quote);

            return (
              <div
                key={quote.id}
                className="glass-card rounded-2xl border border-border/70 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200"
              >
                {/* Main Card Header */}
                <div className="p-5 sm:p-6 bg-surface-alt/30 border-b border-border/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono text-base font-black text-navy px-2.5 py-1 rounded-lg bg-background border border-border/80 shadow-xs">
                      {quote.id}
                    </span>
                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border",
                        statusConfig.badge,
                      )}
                    >
                      <StatusIcon className="h-3.5 w-3.5 shrink-0" />
                      {t(statusConfig.labelKey) || statusConfig.fallbackLabel}
                    </span>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                      <Calendar className="h-3.5 w-3.5" />
                      {new Date(quote.date).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </div>
                  </div>

                  {/* 1-Click Follow-Up Action Buttons */}
                  <div className="flex items-center gap-2">
                    <a
                      href={waUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm transition-all"
                      title="Follow up instantly on WhatsApp with pre-composed reference"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                      <span>{t("account.quotes.followUpWhatsApp") || "WhatsApp"}</span>
                    </a>
                    <a
                      href={mailUrl}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-surface-alt text-navy hover:bg-muted border border-border/80 transition-all"
                      title="Email follow-up with full quote specification"
                    >
                      <Mail className="h-3.5 w-3.5 text-primary" />
                      <span>{t("account.quotes.followUpEmail") || "Email"}</span>
                    </a>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-5 sm:p-6 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="space-y-1.5">
                      <div className="text-xs uppercase tracking-wider font-bold text-mint-ink">
                        {quote.category
                          ? quote.category.replace(/-/g, " ")
                          : t("account.quotes.commercialRfq") || "Commercial RFQ"}
                      </div>
                      <h3 className="font-display text-lg font-bold text-navy">
                        {quote.productTitle || "General Supply Inquiry"}
                      </h3>
                      {quote.contact?.company && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                          <Building2 className="h-3.5 w-3.5" />
                          <span>
                            {quote.contact.company}
                            {quote.contact.country ? ` (${quote.contact.country})` : ""}
                          </span>
                        </div>
                      )}
                    </div>

                    {quote.productSlug && (
                      <a
                        href={
                          quote.category
                            ? `/products/${quote.category}/${quote.productSlug}`
                            : `/products`
                        }
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline shrink-0"
                      >
                        {t("account.quotes.viewProduct") || "View Product Page"}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>

                  {/* Multi-item breakdown if present */}
                  {quote.items && quote.items.length > 0 && (
                    <div className="rounded-xl border border-border/60 bg-surface-alt/40 p-3 space-y-2">
                      <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        {t("account.quotes.itemsRequested") || "Items Included in RFQ"}
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {quote.items.map((item, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between text-xs py-1 border-b border-border/30 last:border-0"
                          >
                            <span className="font-semibold text-navy truncate max-w-[200px]">
                              {item.title}
                            </span>
                            <span className="font-mono font-bold text-mint-ink">
                              Qty: {item.quantity}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Dynamic Technical Specs */}
                  {hasSpecs && (
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 pt-2">
                      {Object.entries(quote.customFields!).map(([key, val]) => (
                        <div
                          key={key}
                          className="rounded-xl border border-border/60 bg-background/60 p-2.5 text-xs space-y-0.5"
                        >
                          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                            {key.replace(/([A-Z])/g, " $1").trim()}
                          </div>
                          <div className="font-semibold text-navy truncate">{val}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Collapsible Message Notes */}
                  {quote.message && (
                    <div className="pt-2">
                      <button
                        onClick={() => toggleExpand(quote.id)}
                        className="flex items-center gap-1.5 text-xs font-bold text-navy/80 hover:text-navy transition-colors"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="h-3.5 w-3.5" />
                            {t("account.quotes.hideNotes") || "Hide Submitted Notes"}
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-3.5 w-3.5" />
                            {t("account.quotes.showNotes") || "Show Submitted Notes & Requirements"}
                          </>
                        )}
                      </button>
                      {isExpanded && (
                        <div className="mt-2.5 rounded-xl border border-border/60 bg-surface-alt/60 p-3.5 text-xs text-navy/90 leading-relaxed font-mono whitespace-pre-wrap">
                          {quote.message}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
